import { useQuery } from 'convex/react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../../../../../convex/_generated/api';

interface DomainDnsRecordsProps {
  domain: string;
}

interface DnsRecord {
  domain: string;
  recordType: 'NS' | 'DS' | 'A' | 'AAAA';
  ttl: number;
  nameserver?: string;
  address?: string;
  keyTag?: number;
  algorithm?: number;
  digestType?: number;
  digest?: string;
  firstSeen?: number;
  lastSeen?: number;
}

function RecordTable({ records, type }: { records: DnsRecord[]; type: string }) {
  if (records.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        No
        {type}
        {' '}
        records found
      </p>
    );
  }

  // Determine columns based on record type
  const getColumns = () => {
    if (type === 'NS') {
      return ['Domain', 'TTL', 'Name Server', 'First Seen', 'Last Seen'];
    }
    if (type === 'A') {
      return ['Domain', 'TTL', 'IPv4 Address', 'First Seen', 'Last Seen'];
    }
    if (type === 'AAAA') {
      return ['Domain', 'TTL', 'IPv6 Address', 'First Seen', 'Last Seen'];
    }
    if (type === 'DS') {
      return ['Domain', 'TTL', 'Key Tag', 'Algorithm', 'Digest Type', 'Digest'];
    }
    return ['Domain', 'TTL', 'Data'];
  };

  const columns = getColumns();

  const formatDate = (timestamp?: number) => {
    if (!timestamp)
      return '—';
    return format(new Date(timestamp), 'MMM d, yyyy');
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col}>{col}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record, index) => (
            <TableRow key={index}>
              {type === 'NS' && (
                <>
                  <TableCell className="font-mono text-xs">{record.domain}</TableCell>
                  <TableCell>{record.ttl}</TableCell>
                  <TableCell className="font-mono text-xs">{record.nameserver}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.firstSeen)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.lastSeen)}</TableCell>
                </>
              )}
              {type === 'A' && (
                <>
                  <TableCell className="font-mono text-xs">{record.domain}</TableCell>
                  <TableCell>{record.ttl}</TableCell>
                  <TableCell className="font-mono text-xs">{record.address}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.firstSeen)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.lastSeen)}</TableCell>
                </>
              )}
              {type === 'AAAA' && (
                <>
                  <TableCell className="font-mono text-xs">{record.domain}</TableCell>
                  <TableCell>{record.ttl}</TableCell>
                  <TableCell className="font-mono text-xs">{record.address}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.firstSeen)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(record.lastSeen)}</TableCell>
                </>
              )}
              {type === 'DS' && (
                <>
                  <TableCell className="font-mono text-xs">{record.domain}</TableCell>
                  <TableCell>{record.ttl}</TableCell>
                  <TableCell>{record.keyTag}</TableCell>
                  <TableCell>{record.algorithm}</TableCell>
                  <TableCell>{record.digestType}</TableCell>
                  <TableCell className="font-mono text-xs">{record.digest}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function DomainDnsRecords({ domain }: DomainDnsRecordsProps) {
  const domainData = useQuery(api.czds.getDomainRecords, { domain });

  // Group records by type
  const domainRecords = {
    ns: (domainData?.records?.filter((r) => r.recordType === 'NS') || []) as DnsRecord[],
    a: (domainData?.records?.filter((r) => r.recordType === 'A') || []) as DnsRecord[],
    aaaa: (domainData?.records?.filter((r) => r.recordType === 'AAAA') || []) as DnsRecord[],
    ds: (domainData?.records?.filter((r) => r.recordType === 'DS') || []) as DnsRecord[],
  };

  const totalRecords = Object.values(domainRecords).reduce((sum, records) => sum + records.length, 0);
  const kind = domainData?.domain?.kind;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">DNS Zone Records</h3>
          {kind && (
            <p className="mt-1 text-sm text-muted-foreground">
              TLD:
              {' '}
              <Badge variant="outline" className={kind === 'bank' ? 'bg-green-50' : 'bg-blue-50'}>
                {kind === 'bank' ? '.Bank' : '.Insurance'}
              </Badge>
            </p>
          )}
        </div>
      </div>

      {domainData?.domain && (
        <div className="text-sm text-muted-foreground">
          First seen:
          {' '}
          {format(new Date(domainData.domain.firstSeen), 'PPP')}
          {' '}
          •
          {' '}
          Last seen:
          {' '}
          {format(new Date(domainData.domain.lastSeen), 'PPP')}
          {' '}
          •
          {' '}
          {totalRecords}
          {' '}
          total records
        </div>
      )}

      {domainData === undefined
        ? (
            <Card className="p-8">
              <p className="text-center text-muted-foreground">Loading DNS records...</p>
            </Card>
          )
        : (domainData === null
            ? (
                <Card className="p-8">
                  <p className="text-center text-muted-foreground">
                    Domain
                    {' '}
                    {domain}
                    {' '}
                    not found in zone file
                  </p>
                </Card>
              )
            : (
                <Tabs defaultValue="ns" className="w-full">
                  <TabsList>
                    <TabsTrigger value="ns">
                      NS Records
                      {' '}
                      <Badge variant="outline" className="ml-2">{domainRecords.ns.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="a">
                      A Records
                      {' '}
                      <Badge variant="outline" className="ml-2">{domainRecords.a.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="aaaa">
                      AAAA Records
                      {' '}
                      <Badge variant="outline" className="ml-2">{domainRecords.aaaa.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="ds">
                      DS Records
                      {' '}
                      <Badge variant="outline" className="ml-2">{domainRecords.ds.length}</Badge>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="ns" className="mt-4">
                    <RecordTable records={domainRecords.ns} type="NS" />
                  </TabsContent>

                  <TabsContent value="a" className="mt-4">
                    <RecordTable records={domainRecords.a} type="A" />
                  </TabsContent>

                  <TabsContent value="aaaa" className="mt-4">
                    <RecordTable records={domainRecords.aaaa} type="AAAA" />
                  </TabsContent>

                  <TabsContent value="ds" className="mt-4">
                    <RecordTable records={domainRecords.ds} type="DS" />
                  </TabsContent>
                </Tabs>
              ))}
    </div>
  );
}
